import i18n from 'helpers/i18n';
import messages from './messages';
import { useEffect, useRef } from 'react';
import { useField, useFormikContext } from 'formik';
import { useAgeLevel } from 'actions/envScales/assessmentTemplates';
import { buildFocusOptions } from 'helpers/dimension';
import {
  MultiSelect,
  MultiSelectValue
} from '@teachstonellc/component-library';

interface FocusDimensionsFieldProps {
  /** Assessment template the observation belongs to. */
  templateId?: number;

  /** When `true`, the age level can no longer change, so neither can the list. */
  isEditing?: boolean;
}

function FocusDimensionsField({
  templateId,
  isEditing
}: FocusDimensionsFieldProps) {
  const { setFieldValue } = useFormikContext();
  const [{ value: ageLevel }] = useField<string>('age_level');
  const [{ value }, meta] = useField<number[]>('dimension_ids');

  const { data: ageLevelData, isPending } = useAgeLevel(templateId, ageLevel);

  // A dimension id only means anything within one age level, so switching
  // tools clears the selection. The ref keeps the first render from clearing
  // what an existing observation was saved with.
  const previousAgeLevel = useRef(ageLevel);

  useEffect(() => {
    if (isEditing || previousAgeLevel.current === ageLevel) {
      return;
    }

    previousAgeLevel.current = ageLevel;
    setFieldValue('dimension_ids', []);
  }, [ageLevel, isEditing, setFieldValue]);

  function handleChange(values: MultiSelectValue[]) {
    setFieldValue(
      'dimension_ids',
      values.map(value => Number(value))
    );
  }

  const options = ageLevelData ? buildFocusOptions(ageLevelData) : [];

  return (
    // MultiSelect hardcodes `min-w-80` on its trigger, a 320px floor that
    // overflows the form's grid column in the narrower sidenav edit view.
    // Clearing it only on the trigger leaves the dropdown panel's own minimum,
    // which is what keeps long labels readable.
    <div className="[&_button]:min-w-0">
      <MultiSelect
        required
        selectAll
        label={i18n.ft(messages.fields.dimensionsOfFocus)}
        placeholder={
          isPending
            ? i18n.ft(messages.fields.dimensionsOfFocusLoading)
            : i18n.ft(messages.fields.dimensionsOfFocusPlaceholder)
        }
        // The library's own defaults are hardcoded English.
        labels={{
          selectAllButton: i18n.ft(messages.fields.selectAll),
          clearAllButton: i18n.ft(messages.fields.clearAll),
          selectedCount: selected =>
            i18n.ft(messages.fields.selectedCount, { selected })
        }}
        options={options}
        values={value ?? []}
        onChange={handleChange}
        error={meta.touched ? (meta.error as string | undefined) : undefined}
      />
    </div>
  );
}

export default FocusDimensionsField;
